import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { CustomerServices } from "../../../core/services/CustomerServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { setSearchString } from "../../../redux/user";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import {
  CustomerFields,
  CustomerListParams,
  Permission,
} from "./CustomerDataTypes";
import "./Customers.css";
import CustomerFilters from "./CustomerFilters";
import { isEmptyVal, isWebResolutionSize } from "../../../utils/helpers";
import ImageCellFormatter from "../../components/Elements/ImageCellFormatter";
import AddressCell from "../../components/Elements/AddressCell";
import LastOrderCell from "../../components/Elements/LastOrderCell";
import FollowUpCell from "../../components/Elements/FollowUpCell";
import PhoneCell from "../../components/Elements/PhoneCell";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import CustomerSingleAction from "./CustomerSingleAction";
import CustomerBulkAction from "./CustomerBulkAction";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import ImageModal from "../../components/Modals/ImageModal/ImageModal";
import { displayField } from "../../../utils/helpers";
import {
  roleTypeArr,
  statusArr,
  messageTypeArr,
  appConstObj,
  operatorsObjArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import moment from "moment";

interface ImageModal {
  openModal: () => void;
  removePic: () => void;
  hideDelete: () => void;
}

function Customers() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<CustomerFields[]>([]);
  const headerCompRef = useRef({});
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const company = useSelector((state: any) => state.company);
  const modalRef = useRef<ImageModal>(null);
  const [selectedCustomer, setSelectedCustomerData] = useState<any>({});
  const [openPictureDelModal, setOpenPictureDelModal] = useState(false);
  const [smartSearchInput, setSmartSearch] = useState<any>("");
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const [isWebResolution, setIsWebResolution] = useState(isWebResolutionSize());
  const dispatch = useDispatch();
  const initSearchFormData = {
    fname: "",
    fname_search_type: "starts",
    lname: "",
    email: "",
    username: "",
    gender: "",
    religion: "",
    store: "",
    emp_code: "",
    profession: "",
    designation: "",
    receive_notification: "",
    mobile: "",
    building: "",
    area: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    address_mobile: "",
    next_follow_up: "",
    category_ids: "",
    customer_source: "",
    event_type: "",
    event_month: moment().format("MM"),
    event_week: "",
    created_by: "",
    customer_rating: "",
    customer_type: "",
    retention_type: "",
    not_ordered_since_val: "1",
    not_ordered_since_unit: "",
    not_followed_up: "",
    has_pending_follow_up: "",
    pending_follow_up: "",
    pending_follow_up_op: operatorsObjArr[0].value,
    home_service_count: "",
    home_service_count_op: operatorsObjArr[0].value,
    activity_type: "",
    activity_interval_val: "",
    activity_date_from: null,
    activity_date_to: null,
    status: "",
  };

  const [formData, setFormData] =
    useState<CustomerListParams>(initSearchFormData);
  const [showCreate, setShowCreate] = useState<boolean>();
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = React.useState<number[]>([]);
  let checked = false;
  let indeterminate = false;
  const [failedImages, setFailedImages] = useState<any>([]);

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };

  const updateResolution = () => {
    setIsWebResolution(isWebResolutionSize());
  };

  useEffect(() => {
    if (company.id) {
      headerCompRef.current = company.id;
    } else {
      headerCompRef.current = "";
    }
    setPage(1);
    getCustomers(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      }),
    );
  }, [company]);

  async function getCustomers(params: CustomerListParams) {
    try {
      setLoading(true);
      const res = await CustomerServices.getCustomers(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function init() {
    try {
      setShowCreate(UtilityServices.hasPermission("create-customer"));
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
    window.addEventListener("resize", updateResolution);
  }, []);

  async function deleteCustomer(customer: any) {
    try {
      let res = await CustomerServices.deleteCustomer(customer?.current?.id);
      setPage(1);
      getCustomers(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        }),
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function publishCustomer(customer: any) {
    try {
      let status =
        customer?.current?.status == statusArr.inactive
          ? statusArr.active
          : statusArr.inactive;
      let res = await CustomerServices.publishCustomer(customer?.current?.id, {
        status,
      });
      setPage(1);
      getCustomers(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        }),
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function bulkPublishAction(status: string) {
    try {
      let res = await CustomerServices.publishBulkCustomer(checkedKeys, {
        status,
      });
      setPage(1);
      getCustomers(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        }),
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function bulkDeleteAction() {
    try {
      let res = await CustomerServices.deleteBulkCustomer(checkedKeys);
      setPage(1);
      getCustomers(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        }),
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function exportCustomerAction() {
    try {
      await CustomerServices.exportCustomers(getSearchParams());
    } catch (error) {
      console.log(error);
    }
  }

  async function deletePic() {
    try {
      setOpenPictureDelModal(false);

      let res = await CustomerServices.deleteCustomerImage(selectedCustomer.id);
      setPage(1);
      getCustomers(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        }),
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function getFailedImages(val: any) {
    setFailedImages(val);
  }

  function customerPicModal(value: any) {
    if (
      !failedImages.includes(value.large_image_url) &&
      value.large_image_url != null &&
      value.large_image_url !== ""
    ) {
      setSelectedCustomerData(value);
      modalRef.current?.openModal();
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...{ company: headerCompRef.current },
      ...data,
    };
    let params: CustomerListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    return params;
  }

  function onPageChange(page: number) {
    setPage(page);
    getCustomers(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      }),
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getCustomers(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      }),
    );
  }

  function submitSearch(formData: any) {
    setFormData(formData);
    getCustomers(getSearchParams(formData));
  }

  function customersAfterFollowup() {
    getCustomers(getSearchParams(formData));
  }

  function processCreateCustomer() {
    if (
      UtilityServices.getLoggedInUser("user_type") == roleTypeArr.backend &&
      !company.id
    ) {
      UtilityServices.showToaster({
        message: t("REQUIRED_SELECT", {
          field: t("COMPANY").toLowerCase(),
        }),
        type: messageTypeArr.error,
      });
      return;
    }
    dispatch(setSearchString(smartSearchInput));
    navigate(ContentRoutes.CREATE_CUSTOMER);
  }

  function clearSearch() {
    setSmartSearch("");
    setFormData(initSearchFormData);
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getCustomers(
      getSearchParams({
        ...initSearchFormData,
        ...{
          page: 1,
          rows: appConstObj.record_per_page,
        },
      }),
    );
  }

  const filterCustomers = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getCustomers(
        getSearchParams({ smart_search: smartSearchInput, rows: rows }),
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getCustomers(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      }),
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getCustomers(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      }),
    );
  };

  const publishPermission = UtilityServices.hasPermission("publish-customer");
  const updatePermission = UtilityServices.hasPermission("update-customer");
  const deletePermission = UtilityServices.hasPermission("delete-customer");
  const permissionObj: Permission = {
    publish: publishPermission,
    update: updatePermission,
    delete: deletePermission,
  };

  return (
    <div className="customers">
      <Container className="container">
        <Header className="header">
          <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={8}>
              <h4>{t("CUSTOMERS")}</h4>
            </FlexboxGrid.Item>
            {showCreate && (
              <FlexboxGrid.Item colspan={4} style={{ textAlign: "end" }}>
                <Button appearance="primary" onClick={processCreateCustomer}>
                  {t("CREATE")}
                </Button>
              </FlexboxGrid.Item>
            )}
          </FlexboxGrid>
        </Header>
        <Content>
          <CustomerFilters onSubmit={submitSearch} onClear={clearSearch} />
          <div className="row">
            <CustomerBulkAction
              checkedKeys={checkedKeys}
              setCheckedKeys={setCheckedKeys}
              onBulkPublish={bulkPublishAction}
              onBulkDelete={bulkDeleteAction}
              onExportCustomer={exportCustomerAction}
            />
            <div className="col-md-4 cst-seaarch">
              <InputGroup>
                <Input
                  className="form-control"
                  placeholder={t("SMART_SEARCH_BY", {
                    entity:
                      t("NAME").toLowerCase() +
                      ", " +
                      t("PHONE").toLowerCase() +
                      ", " +
                      t("OR").toLowerCase() +
                      " " +
                      t("USERNAME").toLowerCase(),
                  })}
                  value={smartSearchInput}
                  onChange={(val: any) => setSmartSearch(val)}
                  onKeyDown={(e: any) => filterCustomers(e)}
                />
                <InputGroup.Button
                  title={t("SEARCH")}
                  onClick={(e: any) => filterCustomers(e)}
                >
                  <SearchIcon />
                </InputGroup.Button>
                <InputGroup.Button
                  title={t("CLEAR")}
                  onClick={() => clearSmartSearch()}
                >
                  <CloseIcon />
                </InputGroup.Button>
              </InputGroup>
            </div>
          </div>

          <div className={total > 0 ? "usr-tbl-border" : "no-record-table"}>
            <Table
              height={400}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
            >
              {isWebResolution && (
                <Column width={30} align="center" fixed>
                  <CustomHeaderCell>
                    <div>
                      <Checkbox
                        inline
                        checked={checked}
                        indeterminate={indeterminate}
                        onChange={handleCheckAll}
                      />
                    </div>
                  </CustomHeaderCell>
                  <CheckCell
                    dataKey="id"
                    checkedKeys={checkedKeys}
                    onChange={handleCheck}
                  />
                </Column>
              )}

              <Column width={50} align="center">
                <CustomHeaderCell>{t("IMAGE")}</CustomHeaderCell>
                <ImageCellFormatter
                  dataKey="large_image_url"
                  rowData={(rowData: any) => rowData}
                  dummyImage={UtilityServices.getLoggedInUser(
                    "user_default_image",
                  )}
                  onFailedImages={getFailedImages}
                  openPictureModal={customerPicModal}
                />
              </Column>
              <Column width={130} sortable resizable>
                <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                <CustomCell dataKey="name">
                  {(rowData: any) => <span>{displayField(rowData.name)}</span>}
                </CustomCell>
              </Column>

              <Column width={100}>
                <CustomHeaderCell>{t("PHONE")}</CustomHeaderCell>
                <PhoneCell
                  positionFromRight="60%"
                  rowData={(rowData: any) => rowData}
                />
              </Column>
              <Column flexGrow={1}>
                <CustomHeaderCell>{t("ADDRESS")}</CustomHeaderCell>
                <AddressCell
                  positionFromRight="40%"
                  rowData={(rowData: any) => rowData}
                />
              </Column>
              <Column width={130} resizable>
                <CustomHeaderCell>{t("STORE")}</CustomHeaderCell>
                <CustomCell dataKey="store_name">
                  {(rowData: any) => (
                    <span>{displayField(rowData.store_name)}</span>
                  )}
                </CustomCell>
              </Column>
              <Column width={100}>
                <CustomHeaderCell>
                  {t("LAST")} {t("ORDER")}
                </CustomHeaderCell>
                <LastOrderCell rowData={(rowData: any) => rowData} />
              </Column>
              <Column width={120}>
                <CustomHeaderCell>
                  {t("FOLLOW")} {t("UP")} {t("ON")}
                </CustomHeaderCell>
                <FollowUpCell
                  positionFromRight="20%"
                  rowData={(rowData: any) => rowData}
                  onFollowup={customersAfterFollowup}
                />
              </Column>
              <Column width={isWebResolution ? 160 : 50} fixed="right">
                <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
                <Cell>
                  {(rowData) => (
                    <CustomerSingleAction
                      customerData={rowData}
                      onDeleteCustomer={deleteCustomer}
                      onPublishCustomer={publishCustomer}
                      onFollowup={customersAfterFollowup}
                      permission={permissionObj}
                    />
                  )}
                </Cell>
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total", "-", "limit", "|", "pager", "skip"]}
            total={total}
            limitOptions={appConstObj.limit_options}
            limit={rows}
            activePage={page}
            onChangePage={onPageChange}
            onChangeLimit={handleChangeRows}
          />
        </Footer>
        <ToastContainer />
      </Container>
      <ImageModal
        ref={modalRef}
        modalTitle={selectedCustomer.name}
        heading={selectedCustomer.name}
        body={
          <img
            src={selectedCustomer.large_image_url}
            className="main-profile-pic"
            alt={t("PROFILE_PIC")}
          />
        }
        removePic={() => setOpenPictureDelModal(true)}
      />
      <AlertModal
        open={openPictureDelModal}
        handleClose={(e: any) => {
          if (e == true) {
            deletePic();
          } else {
            setOpenPictureDelModal(false);
            setSelectedCustomerData({});
          }
        }}
        displayText={t("CONFIRM_ACTION", {
          action: t("DELETE").toLowerCase(),
          entity: t("PROFILE_PIC").toLowerCase(),
        })}
        modalTitle={t("CONFIRM_ACTION_TITLE", {
          action: t("DELETE").toLowerCase(),
        })}
      />
    </div>
  );
}

export default Customers;
